<template>
  <q-card style="width: 800px; max-width: 800px;">
    <q-card-section class="row items-center">
      <div class="text-h6">Application Audit Logs</div>
      <q-space />
      <q-btn icon="close" flat round dense v-close-popup />
    </q-card-section>
    <q-card-section class="q-pb-none">
      <div class="row q-col-gutter-sm">
        <!-- Field Name Filter -->
        <div class="col-12 col-md-6">
          <q-select
            v-model="filterFieldName"
            :options="fieldOptions"
            label="Field Name"
            outlined
            dense
            clearable
          >
            <template v-slot:option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ formatCamelCase(scope.opt) }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
            <template v-slot:selected>
              <span>{{ formatCamelCase(filterFieldName) }}</span>
            </template>
          </q-select>
        </div>

        <!-- Date Range -->
        <div class="col-12 col-md-6">
          <q-input
            v-model="dateRangeInput"
            outlined
            dense
            label="Date Range"
            readonly
          >
            <template v-slot:append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy ref="qDateProxy"
                 cover transition-show="scale" transition-hide="scale">
                  <q-date
                    v-model="dateRange"
                    range
                    minimal
                  >
                    <div class="row items-center justify-end q-gutter-sm">
                      <q-btn label="Clear" color="primary" flat v-close-popup @click="clearDates" />
                      <q-btn label="Apply" color="primary" flat v-close-popup />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
        </div>
      </div>

      <div class="row q-mt-sm">
        <q-btn color="primary"
        label="Apply Filters"
        icon="filter_alt"
        size="md"
        class="q-mr-sm"
        @click="applyFilters" />
        <q-space />
        <q-btn color="primary"
        label="Reset"
        icon="refresh"
        flat
        size="md"
        @click="resetFilters" />
        <q-btn
          color="primary"
          icon="download"
          flat
          size="md"
          label="Export CSV"
          :disable="filteredLogs.length === 0"
          @click="exportToCSV"
        />
      </div>
    </q-card-section>

    <q-card-section class="q-pa-none">
      <q-table
        :rows="filteredLogs"
        :columns="logsColumns"
        row-key="auditLogId"
        class="q-mt-md"
        dense
        flat
        persistent="true"
        v-model:pagination="pagination"
        :loading="loading"
      >
        <template v-slot:no-data>
          <div class="full-width row flex-center q-pa-md">
            <div class="text-grey-8">
              {{ loading ? 'Fetching logs...' : 'No logs found for this application.' }}
            </div>
          </div>
        </template>
        <template v-slot:body-cell-oldValue="props">
          <q-td :props="props">
            {{ formatCamelCase(props.value) }}
          </q-td>
        </template>
        <template v-slot:body-cell-newValue="props">
          <q-td :props="props">
            {{ formatCamelCase(props.value) }}
          </q-td>
        </template>
        <template v-slot:body-cell-fieldName="props">
          <q-td :props="props">
            {{ formatCamelCase(props.value) }}
          </q-td>
        </template>
      </q-table>
    </q-card-section>

    <q-card-actions align="right">
      <q-btn flat label="Close" color="primary" v-close-popup />
    </q-card-actions>
  </q-card>
</template>

<script>
import {
  ref, onMounted, computed, watch,
}
from 'vue';
import { date } from 'quasar';
import useUtilities from '@/utilities/uiUtilities';
import useUiValidations from '@/utilities/uiValidations';
import useApplicationLogs from '../../hooks/applicationLogs';

export default {
  name: 'ApplicationLogs',
  props: {
    applicationId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { notifyResult, createDownloadableFile, formatPacificTime } = useUtilities();
    const { logs, filteredLogs, loading, fetchLogs } = useApplicationLogs(notifyResult); //eslint-disable-line
    const { formatCamelCase } = useUiValidations();

    const pagination = ref({
      rowsPerPage: 10,
      sortBy: 'timestamp',
      descending: true,
    });

    // Filters
    const filterFieldName = ref(null);
    const dateRange = ref({
      from: null,
      to: null,
    });

    // Field names for audit logs
    const fieldOptions = [
      'Status',
      'WaitlistStatus',
      'ResidencyStatus',
      'DssAccessible',
      'Agreement',
      'HandbookAgreement',
    ];

    const formatDate = (dateString) => { return date.formatDate(dateString, 'MM/DD/YYYY'); }; // eslint-disable-line

    const dateRangeInput = computed(() => {
      if (dateRange.value.from && dateRange.value.to) {
        return `${formatDate(dateRange.value.from)} - ${formatDate(dateRange.value.to)}`;
      } if (dateRange.value.from) {
        return `${formatDate(dateRange.value.from)} - `;
      } if (dateRange.value.to) {
        return ` - ${formatDate(dateRange.value.to)}`;
      }
      return '';
    });

    const logsColumns = [
      {
        name: 'timestamp',
        required: true,
        label: 'Timestamp',
        align: 'left',
        field: (row) => row.changedAt,
        format: (val) => formatPacificTime(val),
        sortable: true,
      },
      {
        name: 'fieldName',
        required: true,
        label: 'Field Name',
        align: 'left',
        field: 'fieldName',
        sortable: true,
      },
      {
        name: 'oldValue',
        required: true,
        label: 'Previous Value',
        align: 'left',
        field: 'oldValue',
        sortable: true,
      },
      {
        name: 'newValue',
        required: true,
        label: 'New Value',
        align: 'left',
        field: 'newValue',
        sortable: true,
      },
      {
        name: 'changedBy',
        required: true,
        label: 'Changed By',
        align: 'left',
        field: 'username',
        sortable: true,
      },
    ];

    const applyFilters = () => {
      // Apply all active filters to the logs
      filteredLogs.value = logs.value.filter(log => { // eslint-disable-line
        let matches = true;

        // Filter by field name
        if (filterFieldName.value && log.fieldName !== filterFieldName.value) {
          matches = false;
        }

        // Filter by date range
        if (dateRange.value.from) {
          const startDate = new Date(dateRange.value.from);
          startDate.setHours(0, 0, 0, 0);
          if (new Date(log.changedAt) < startDate) {
            matches = false;
          }
        }

        if (dateRange.value.to) {
          const endDate = new Date(dateRange.value.to);
          endDate.setHours(23, 59, 59, 999);
          if (new Date(log.changedAt) > endDate) {
            matches = false;
          }
        }

        return matches;
      });
    };

    const resetFilters = () => {
      filterFieldName.value = null;
      dateRange.value = {
        from: null,
        to: null,
      };
      filteredLogs.value = [...logs.value];
    };

    const clearDates = () => {
      dateRange.value = {
        from: null,
        to: null,
      };
    };

    // Export logs to CSV
    const exportToCSV = () => {
      if (filteredLogs.value.length === 0) return;

      let csvContent = 'Timestamp,Field Name,Previous Value,New Value,Changed By\n';

      filteredLogs.value.forEach((log) => {
        const row = [
          formatPacificTime(log.changedAt),
          formatCamelCase(log.fieldName),
          formatCamelCase(log.oldValue),
          formatCamelCase(log.newValue),
          log.username,
        ];

        // Handle commas and quotes in CSV
        const escapedRow = row.map(field => { // eslint-disable-line
          if (field === null || field === undefined) return '';
          const stringField = String(field);
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        });

        csvContent += escapedRow.join(',') + '\n'; // eslint-disable-line
      });

      const fileName = `application_${props.applicationId}_logs.csv`;
      createDownloadableFile(csvContent, fileName, 'text/csv;charset=utf-8;');
    };

    watch(() => props.applicationId, (newId) => {
      if (newId) {
        (async () => {
          await fetchLogs(newId);
          resetFilters();
        })();
      }
    });

    onMounted(() => {
      if (props.applicationId) {
        (async () => {
          await fetchLogs(props.applicationId);
        })();
      }
    });

    return {
      logs,
      filteredLogs,
      loading,
      logsColumns,
      pagination,
      fetchLogs,
      formatCamelCase,
      // Filter related
      filterFieldName,
      fieldOptions,
      dateRange,
      dateRangeInput,
      applyFilters,
      resetFilters,
      clearDates,
      formatDate,
      exportToCSV,
    };
  },
};
</script>

<style scoped>
.filter-heading {
  font-weight: 500;
  margin-bottom: 8px;
}
</style>
